import { format, parseISO } from 'date-fns';

/**
 * Centralized date utilities for consistent date handling across the app.
 * All dates should be treated as local dates unless explicitly working with UTC.
 */

/**
 * Parse a date string (YYYY-MM-DD or ISO 8601) as a local date at noon to avoid timezone shifts.
 * Use this when parsing date-only strings from the API or form inputs.
 */
export function parseLocalDate(dateStr: string): Date {
  if (!dateStr) return new Date();

  // Handle ISO 8601 format (e.g., "2025-12-30T00:00:00Z" or "2025-12-30T00:00:00")
  if (dateStr.includes('T')) {
    // Parse the date part only, ignoring time and timezone
    const datePart = dateStr.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    return new Date(year, month - 1, day, 12, 0, 0);
  }

  // Handle simple YYYY-MM-DD format
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day, 12, 0, 0);
}

/**
 * Format a date as YYYY-MM-DD in local time.
 * Use this when sending dates to the API or storing date strings.
 */
export function formatDateForApi(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/**
 * Format a date for display with a consistent US locale.
 * Example: "Dec 29, 2025"
 */
export function formatDateDisplay(date: Date | string): string {
  const d = typeof date === 'string' ? parseLocalDate(date) : date;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format a date for display with full month name.
 * Example: "December 29, 2025"
 */
export function formatDateDisplayFull(date: Date | string): string {
  const d = typeof date === 'string' ? parseLocalDate(date) : date;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format a date for display as short date.
 * Example: "12/29/2025"
 */
export function formatDateShort(date: Date | string): string {
  const d = typeof date === 'string' ? parseLocalDate(date) : date;
  return d.toLocaleDateString('en-US');
}

/**
 * Parse an ISO date string from the API (which includes time and timezone)
 * and return a Date object.
 */
export function parseApiDate(isoString: string): Date {
  if (!isoString) return new Date();
  return parseISO(isoString);
}

/**
 * Get today's date as YYYY-MM-DD string in local time.
 */
export function getTodayString(): string {
  return formatDateForApi(new Date());
}

/**
 * Get the current year in local time.
 */
export function getCurrentYear(): number {
  return new Date().getFullYear();
}

/**
 * Compare two date strings (YYYY-MM-DD format).
 * Returns negative if a < b, positive if a > b, 0 if equal.
 */
export function compareDateStrings(a: string, b: string): number {
  return a.localeCompare(b);
}
